import React from 'react';
import { useOrderDetailsScreen } from './hooks/useOrderDetailsScreen';
import { LoadingSpinner, SVGS } from '@zonofi/zono-money-design-system';
import { getCalculatedAmount, getFormattedAmount } from '@zonofi/common';
import {
  MoqErrorSnackBar,
  onClickBackToWhatsapp,
  OrderDetailsEmptyCart,
  OrderDetailsList,
  OrderDetailsOtherTab,
  OrderDetailsSummary,
  OrderStatusTag,
  PromotionsModal,
  TermAndConditionModal,
} from '@zonofi/zono-money-whatsapp-stack';
import './order-details-screen.scss';
import { useLocation } from 'react-router-dom';
import {
  getCustomerInvite,
  getIsSimulation,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { sendWAEventToParent } from '@zonofi/zono-money-helpers';
import moment from 'moment';

export const OrderDetailsScreenV2: React.FC = () => {
  const location = useLocation();
  const isCart = location.pathname?.includes('cart');
  const isSimulation = useAppSelector(getIsSimulation);
  const inviteDetails = useAppSelector(getCustomerInvite);

  const { seller, inviteId, waConfig, buyer } = inviteDetails;
  const {
    orderDetails,
    onChangeQuantity,
    onIncrement,
    onDecrement,
    onDelete,
    onConfirm,
    onAddMore,
    isLoading,
    isSubmitting,
    fetchOrderResponse,
    showImage,
    showPrice,
    showOutOfStock,
    showOrderEdit,
    cartAPIResponse,
    cartSummaryAPIResponse,
    error,
    onBackBtnClick,
    moqError,
    hideGoBack,
    primaryActionBtnText,
    selectedSection,
    setSelectedSection,
    formatShippingAddress,
    showCancelBtn,
    onCancelOrder,
    showFooter,
    showAdvancePayment,
    openDrawer,
    isGettingConfig,
    showPromotionsModal,
    promotionSchemes,
    hasAvailablePromotions,
    selectedBenefit,
    onApplyPromotion,
    onRemoveBenefit,
    onSkipPromotion,
    onCancelPromotion,
    onApplyDiscount,
    showToastError,
    showToastSuccess,
    promotionApplied,
    ledgerSummary,
    isShowOutstandingAmountAndCreditLimit,
    onTnCPress,
    showTnCModal,
    onCloseTnCModal,
    advanceAmount,
  } = useOrderDetailsScreen();

  const hasOutOfStockLine = Boolean(
    showOutOfStock &&
      orderDetails?.lines?.some((line) => Number(line?.stock ?? 1) < 1)
  );

  const hasAllUnmappedProducts = orderDetails?.lines?.every((line) =>
    line?.status?.toLowerCase()?.includes('unmapped')
  );

  if (error?.show) {
    return (
      <div className="order-details-screen-v2-error-screen font-primary">
        <span>{error?.msg}</span>
      </div>
    );
  }

  if (
    orderDetails?.lines?.length === 0 &&
    (fetchOrderResponse?.isSuccess || cartAPIResponse?.isSuccess)
  ) {
    return <OrderDetailsEmptyCart />;
  }

  return (
    <div className="order-details-screen-v2 font-primary">
      <div className="order-details-header">
        {!isLoading && (
          <>
            <div className="order-details-header-title">
              {isCart && !hideGoBack && (
                <img
                  src={SVGS.LeftArrowBlack}
                  alt=""
                  onClick={onBackBtnClick}
                />
              )}

              {!isCart && !isSimulation && (
                <div
                  className="catalog-header-seller-logo-container"
                  onClick={openDrawer}
                >
                  {seller?.profileImg ? (
                    <img
                      src={seller?.profileImg}
                      className="catalog-header-seller-logo-container-img"
                    />
                  ) : (
                    <img src={SVGS.UserProfileIcon} />
                  )}
                </div>
              )}

              <span className=" font-primary-semibold">
                {isCart ? 'Cart' : 'Order Details'}
              </span>
              <div>
                <OrderStatusTag status={orderDetails?.status} />
              </div>
            </div>
            {showOrderEdit && (
              <div
                className="order-details-header-add-more font-primary-semibold"
                onClick={onAddMore}
              >
                Add More <img src={SVGS.PlusIconBlue} />
              </div>
            )}
          </>
        )}
      </div>
      {showToastError?.show && (
        <div className="order-details-screen-v2-toast-error font-primary">
          <span>{showToastError?.msg}</span>
        </div>
      )}
      {showToastSuccess?.show && (
        <div className="order-details-screen-v2-toast-success font-primary">
          <span>{showToastSuccess?.msg}</span>
        </div>
      )}

      {isLoading ||
        (isGettingConfig && (
          <div className="order-details-loading">
            <LoadingSpinner />
          </div>
        ))}

      {!isLoading && !isGettingConfig && (
        <div className="order-details-body">
          <div className="order-details-sections">
            <span
              className={`order-details-sections-item font-primary-medium ${
                selectedSection === 0 ? 'selected-section' : ''
              }`}
              onClick={() => {
                if (selectedSection !== 0) {
                  setSelectedSection(0);
                }
              }}
            >
              Items
            </span>
            <span
              className={`order-details-sections-item font-primary-medium ${
                selectedSection === 1 ? 'selected-section' : ''
              }`}
              onClick={() => {
                if (selectedSection !== 1) {
                  setSelectedSection(1);
                }
              }}
            >
              Other Details
            </span>
          </div>
          {selectedSection === 0 && (
            <>
              {isShowOutstandingAmountAndCreditLimit && (
                <div
                  className="order-details-line-items-container"
                  style={{ gap: '8px' }}
                >
                  <div className="order-deatils-ourstanding-amount-tag">
                    <div className="font-primary-semibold">
                      {`Your Outstanding Amount is ${`${getCalculatedAmount(
                        ledgerSummary?.[0]?.outstandingAmount
                      )}`}`}
                    </div>
                  </div>
                  <div className="order-deatils-credit-limit-text font-primary-semibold">
                    {`as of ${moment(ledgerSummary?.[0]?.latestLedgerUpdatedAt).format('DD MMMM YYYY')}. You have an available credit limit of ${getCalculatedAmount(ledgerSummary?.[0]?.grossCreditLimit)}, which can be used to place orders.`}
                  </div>
                </div>
              )}

              <div className="order-details-line-items-container">
                <div className="order-details-line-items-header font-primary-semibold">
                  <img src={SVGS.CartBox} />
                  <span>
                    {orderDetails?.lines?.length < 10
                      ? orderDetails?.lines?.length.toString().padStart(2, '0')
                      : orderDetails?.lines?.length}{' '}
                    items
                  </span>
                </div>
                <div className="order-details-line-items">
                  <OrderDetailsList
                    orderDetails={orderDetails}
                    onDecrement={onDecrement}
                    onDelete={onDelete}
                    onIncrement={onIncrement}
                    onChangeQuantity={onChangeQuantity}
                    showImage={showImage}
                    showPrice={showPrice}
                    showOrderEdit={showOrderEdit}
                    showOutOfStock={showOutOfStock}
                    isCart={isCart}
                  />
                </div>
              </div>
              {showOrderEdit && (
                <>
                  {!selectedBenefit &&
                    hasAvailablePromotions &&
                    promotionApplied && (
                      <div
                        className="order-details-footer-apply-benefits font-primary-semibold"
                        onClick={onApplyDiscount}
                      >
                        <img src={SVGS.SchemePercentIcon} alt="" />
                        <span>Apply Benefits</span>
                        <img src={SVGS.RightArrowBlue} alt="" />
                      </div>
                    )}
                  {selectedBenefit && (
                    <div className="order-details-selected-benefit">
                      <div className="order-details-selected-benefit-content">
                        <img src={SVGS.DiscountIcon} alt="benefit" />

                        <div className="order-details-selected-benefit-content-text">
                          <span className="font-primary-semibold">
                            {selectedBenefit?.name}
                          </span>
                          {selectedBenefit.description ? (
                            <span className="font-primary-regular">
                              {selectedBenefit?.description}
                            </span>
                          ) : null}
                          <span className="order-details-selected-benefit-meta font-primary">
                            {selectedBenefit?.promotionCode}
                            {selectedBenefit?.minOrderQuantity
                              ? ` • Min Order Quantity: ${selectedBenefit?.minOrderQuantity}`
                              : ''}
                          </span>
                          <button
                            className="order-details-selected-benefit-remove font-primary-semibold"
                            onClick={onApplyDiscount}
                          >
                            Change benefit
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {isCart && cartSummaryAPIResponse?.data ? (
                <div className="order-details-cart-bill-summary">
                  <div className="order-details-cart-bill-summary-header font-primary-semibold">
                    <img src={SVGS.InvoiceIconOutlineBlack} alt="" />
                    <span>Bill Summary</span>
                  </div>
                  <div className="order-details-cart-bill-summary-content font-primary">
                    <div className="order-details-cart-bill-summary-section">
                      <div className="order-details-cart-bill-summary-row">
                        <span>Total Items</span>
                        <span>
                          {cartSummaryAPIResponse?.data?.totalSKUCount || 0}
                        </span>
                      </div>
                      <div className="order-details-cart-bill-summary-row">
                        <span>Total Quantity</span>
                        <span>
                          {cartSummaryAPIResponse?.data?.totalItemsCount || 0}
                        </span>
                      </div>
                    </div>

                    <div className="order-details-cart-bill-summary-section">
                      <div className="order-details-cart-bill-summary-row savings">
                        <span>Scheme Savings</span>
                        <span>
                          {getFormattedAmount(
                            -(cartSummaryAPIResponse?.data?.totalDiscount || 0)
                          )}
                        </span>
                      </div>
                      <div className="order-details-cart-bill-summary-row muted">
                        <span>Total Tax</span>
                        <span>
                          {getFormattedAmount(
                            cartSummaryAPIResponse?.data?.totalTax || 0
                          )}
                        </span>
                      </div>
                      <div className="order-details-cart-bill-summary-row total-order-value">
                        <span>Total Order Value</span>
                        <span>
                          {getFormattedAmount(
                            cartSummaryAPIResponse?.data?.totalOrderValue || 0
                          )}
                        </span>
                      </div>

                      {/* <div className="order-details-cart-bill-summary-row total-order-value">
                        <span>Shipping Charges</span>
                        <span>
                          {getFormattedAmount(
                            230 
                          )}
                        </span>
                      </div> */}
                    </div>

                    <div className="order-details-cart-bill-summary-row grand-total">
                      <div className="order-details-cart-bill-summary-row grand-total-content">
                        <span>Total</span>
                        <small>Incl. all taxes and charges</small>
                      </div>
                      <span>
                        {getFormattedAmount(
                          cartSummaryAPIResponse?.data?.grossOrderValue ||
                            cartSummaryAPIResponse?.data?.totalOrderValue ||
                            0
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <OrderDetailsSummary
                  orderDetails={orderDetails}
                  showOrderEdit={showOrderEdit}
                  showPrice={showPrice}
                />
              )}
              {isSimulation && !showOrderEdit && (
                <button
                  className="backto_whatsapp font-primary-semibold"
                  onClick={() => {
                    onClickBackToWhatsapp({
                      isSimulation: isSimulation,
                      sendEventToParent: () => {
                        sendWAEventToParent({ orderSuccessful: true });
                      },
                    });
                  }}
                >
                  Back to WhatsApp
                </button>
              )}
              {moqError.isError && (
                <div className="order-details-moq-error-container">
                  <MoqErrorSnackBar number={moqError.quantity} />
                </div>
              )}
            </>
          )}
          {selectedSection === 1 && (
            <OrderDetailsOtherTab
              orderDetails={orderDetails}
              formatShippingAddress={formatShippingAddress}
            />
          )}
        </div>
      )}

      {!isLoading && showFooter && (
        <div className="order-details-footer">
          {showAdvancePayment && (
            <div className="order-details-footer-payment-view">
              <div className="order-details-footer-payment-view-left-content">
                <img src={SVGS.InfoWhiteIcon} />
                <div className="order-details-footer-payment-view-text font-primary-semibold">
                  Seller has requested an advance payment of{' '}
                  <span className="font-primary-bold">{advanceAmount}</span> to
                  accept this order.
                </div>
              </div>

              <div
                onClick={onTnCPress}
                className="order-details-footer-payment-view-tnc font-primary-semibold"
              >
                View T&C
              </div>
            </div>
          )}
          {showAdvancePayment && isCart ? (
            <>
              <div className="padding-space">
                <div
                  className="order-details-footer-actions-btn primary font-primary-semibold"
                  onClick={onConfirm}
                >
                  <span>
                    {showAdvancePayment
                      ? 'Check Out & Pay Advance Amount'
                      : 'Pay Now'}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="padding-space">
              {!isSimulation && (
                <span className="order-details-footer-warning">
                  Estimate for your requirement will be available after the
                  confirmation!
                </span>
              )}
              {hasOutOfStockLine && (
                <div className="order-details-footer-oos-warning">
                  <span className="font-primary-semibold">
                    Your cart contains an out-of-stock item. Please remove it to
                    continue.
                  </span>
                </div>
              )}
              <div className="order-details-footer-actions">
                {showCancelBtn && (
                  <div
                    className="order-details-footer-actions-btn cancel font-primary-semibold"
                    onClick={onCancelOrder}
                  >
                    Cancel
                  </div>
                )}
                <div
                  className={`order-details-footer-actions-btn primary font-primary-semibold ${
                    isSubmitting || hasOutOfStockLine || hasAllUnmappedProducts
                      ? 'order-details-footer-actions-btn-disabled'
                      : ''
                  }`}
                  onClick={() => {
                    if (primaryActionBtnText === 'Back To Whatsapp') {
                      onClickBackToWhatsapp({
                        isSimulation: isSimulation,
                        sendEventToParent: () => {
                          sendWAEventToParent({ orderSuccessful: true });
                        },
                      });
                    } else {
                      isSubmitting || hasOutOfStockLine
                        ? () => {}
                        : hasAvailablePromotions
                          ? promotionApplied
                            ? onConfirm()
                            : onApplyDiscount()
                          : onConfirm();
                    }
                  }}
                >
                  <span>
                    {isSubmitting
                      ? 'Please wait...'
                      : hasOutOfStockLine
                        ? 'Out Of Stock Items In Cart'
                        : primaryActionBtnText}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showPromotionsModal && (
        <PromotionsModal
          schemes={promotionSchemes}
          onApply={(val) => {
            onApplyPromotion(val);
          }}
          selectedBenefit={selectedBenefit}
          onSkip={() => {
            onSkipPromotion();
          }}
          onCancel={onCancelPromotion}
          isApplying={isSubmitting}
          modalTitle={'Cash Discount Benefit'}
        />
      )}

      {showTnCModal && (
        <TermAndConditionModal
          isVisible={showTnCModal}
          handleClose={onCloseTnCModal}
          termNcondition={orderDetails?.paymentTerm || 'N/A'}
        />
      )}
    </div>
  );
};
